import { useQuery } from '@tanstack/react-query';
import { sub } from 'date-fns';
import { useEffect } from 'react';
import { auditLogQueryKeys, AuditLogs } from '#api';
import { usePrevious } from '#hooks/usePrevious.ts';
import type { TargetOption, TimeFrame } from '../../form-types.ts';

type Props = {
  targetType?: TargetOption;
  timeFrame?: TimeFrame;
  targetId?: string;
};

export function useGetAuditLogsBlobURL({ targetType: chosenTarget, timeFrame, targetId }: Props) {
  // We won't actually send a request when timeFrame is undefined, but we need to handle the case for typescript
  const daysAgo = Number(timeFrame ?? '0');
  const since = getDateFromDaysAgo(daysAgo);
  const targetType = chosenTarget === 'all' ? undefined : chosenTarget;

  const query = useQuery({
    // Setting the query key to use `since` means that our query key updates every second, but we only need per-day granularity here.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: auditLogQueryKeys.csv({ targetType, timeFrame, targetId }),
    queryFn: ({ signal }) => AuditLogs.getAuditLogsBlobURL({ targetType, since, targetId }, { signal }),
    enabled: Boolean(timeFrame),
    staleTime: 3000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });

  const previousBlobUrl = usePrevious(query.data);

  // Free up memory if there was an old url already stored
  useEffect(() => {
    if (query.isSuccess && previousBlobUrl) {
      URL.revokeObjectURL(previousBlobUrl);
    }
  }, [previousBlobUrl, query.isSuccess]);

  return query;
}

/**
 * Calculate a date a provided number of days in the past.
 */
function getDateFromDaysAgo(days: number) {
  return sub(new Date(), { days });
}
